/**
 * ToastProvider — the shared global toast/notification system.
 *
 * The single notification surface for the app: creation confirmations,
 * sync results, background job outcomes, etc. Styled with the shared
 * design-system tokens (dark surface, status-colored accent stripe) so
 * toasts read as part of the app rather than floating alerts.
 *
 * Why custom (not sonner / react-hot-toast):
 *   Yarn workspaces in this monorepo can't currently add new deps
 *   cleanly (a broken @react-native/eslint-config cross-reference
 *   blocks resolution). Writing ~150 lines in-house unblocks the work
 *   and avoids importing another runtime dep. When the workspace gets
 *   fixed and sonner adoption is wanted, this module can be
 *   replaced behind the same `useToast()` API without touching call
 *   sites.
 *
 * Usage:
 *
 *   // 1. Wrap the app once (web: apps/web/src/index.tsx):
 *   <ToastProvider>
 *     <App />
 *   </ToastProvider>
 *
 *   // 2. In any component:
 *   const toast = useToast();
 *   toast.success('Saved');
 *   toast.error('Save failed', { description: err.message });
 *   toast.success('Work order created', {
 *     action: { label: 'Open work order', onClick: () => navigateToWorkOrder(id) },
 *   });
 *
 *   // 3. Async helper:
 *   toast.promise(saveBatch(), {
 *     loading: 'Saving…',
 *     success: 'Saved',
 *     error: (e) => `Save failed: ${e.message}`,
 *   });
 *
 * Toasts auto-dismiss after a few seconds (overridable; errors stay
 * longer). Hover pauses dismissal. Container has role="status" +
 * aria-live="polite" so screen readers announce new toasts.
 *
 * DOM-rendered — web only. A React Native renderer would need its own
 * container behind the same ToastApi.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { theme } from '../../theme';

type ToastVariant = 'success' | 'error' | 'info' | 'warning' | 'loading';

interface ToastOptions {
  /** Auto-dismiss after this many ms. 0 disables auto-dismiss. */
  duration?: number;
  /** Secondary body line under the headline. */
  description?: string;
  /** Optional action link rendered under the message; clicking it also dismisses the toast. */
  action?: { label: string; onClick: () => void };
}

interface Toast extends ToastOptions {
  id: string;
  message: string;
  variant: ToastVariant;
  createdAt: number;
}

export interface ToastApi {
  success: (message: string, opts?: ToastOptions) => string;
  error: (message: string, opts?: ToastOptions) => string;
  info: (message: string, opts?: ToastOptions) => string;
  warning: (message: string, opts?: ToastOptions) => string;
  /** Show a pending toast that resolves to success/error based on the promise. */
  promise: <T>(
    p: Promise<T>,
    msgs: { loading: string; success: string | ((v: T) => string); error: string | ((e: unknown) => string) }
  ) => Promise<T>;
  /** Manually dismiss a toast by id. */
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | undefined>(undefined);

const DEFAULT_DURATION_MS: Record<ToastVariant, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 8000,
  loading: 0, // until resolved
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string, opts?: ToastOptions): string => {
      const id = Math.random().toString(36).slice(2);
      const duration = opts?.duration ?? DEFAULT_DURATION_MS[variant];
      const toast: Toast = { id, message, variant, createdAt: Date.now(), ...opts, duration };
      setToasts((prev) => [...prev, toast]);
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration)
        );
      }
      return id;
    },
    [dismiss]
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (m, o) => push('success', m, o),
      error: (m, o) => push('error', m, o),
      info: (m, o) => push('info', m, o),
      warning: (m, o) => push('warning', m, o),
      dismiss,
      promise: async <T,>(
        p: Promise<T>,
        msgs: { loading: string; success: string | ((v: T) => string); error: string | ((e: unknown) => string) }
      ) => {
        const loadingId = push('loading', msgs.loading, { duration: 0 });
        try {
          const v = await p;
          dismiss(loadingId);
          push('success', typeof msgs.success === 'function' ? msgs.success(v) : msgs.success);
          return v;
        } catch (e) {
          dismiss(loadingId);
          push('error', typeof msgs.error === 'function' ? msgs.error(e) : msgs.error);
          throw e;
        }
      },
    }),
    [push, dismiss]
  );

  // Pause auto-dismiss while user hovers; resume on leave.
  const onMouseEnter = useCallback((id: string) => {
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
  }, []);
  const onMouseLeave = useCallback(
    (id: string) => {
      const toast = toasts.find((t) => t.id === id);
      if (!toast || !toast.duration) return;
      const elapsed = Date.now() - toast.createdAt;
      const remaining = Math.max(1000, toast.duration - elapsed);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), remaining)
      );
    },
    [toasts, dismiss]
  );

  useEffect(() => {
    // Clean up any pending timers if the provider unmounts (HMR).
    return () => {
      timers.current.forEach((t) => clearTimeout(t));
      timers.current.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} onHoverEnter={onMouseEnter} onHoverLeave={onMouseLeave} />
    </ToastContext.Provider>
  );
};

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast() must be called inside a <ToastProvider>. Wrap your app root once.');
  }
  return ctx;
}

/**
 * Toast API when there is a provider, null when there isn't.
 *
 * For leaf components that only *optionally* report a failure and are rendered
 * in contexts without an app root — note bodies inside isolated component
 * tests, for instance. Prefer useToast() anywhere a provider is guaranteed.
 */
export function useOptionalToast(): ToastApi | null {
  return useContext(ToastContext) ?? null;
}

// ── Rendering ─────────────────────────────────────────────────────────────

// Toasts share the app's dark card surface; the variant shows as a
// status-colored accent stripe + dot rather than a solid colored body.
const VARIANT_ACCENT: Record<ToastVariant, string> = {
  success: theme.colors.status.success,
  error: theme.colors.status.error,
  info: theme.colors.status.info,
  warning: theme.colors.status.warning,
  loading: theme.colors.status.neutral,
};

const ToastContainer: React.FC<{
  toasts: Toast[];
  onDismiss: (id: string) => void;
  onHoverEnter: (id: string) => void;
  onHoverLeave: (id: string) => void;
}> = ({ toasts, onDismiss, onHoverEnter, onHoverLeave }) => {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="false"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: theme.zIndex.toast,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        maxWidth: 'min(420px, calc(100vw - 48px))',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => {
        const accent = VARIANT_ACCENT[t.variant];
        return (
          <div
            key={t.id}
            onMouseEnter={() => onHoverEnter(t.id)}
            onMouseLeave={() => onHoverLeave(t.id)}
            style={{
              backgroundColor: theme.colors.background.tertiary,
              color: theme.colors.text.primary,
              border: `1px solid ${theme.colors.border.light}`,
              borderLeft: `3px solid ${accent}`,
              padding: '12px 16px',
              borderRadius: 8,
              boxShadow: '0 8px 28px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              pointerEvents: 'auto',
              animation: 'tc-toast-in 200ms ease-out',
              fontFamily: 'system-ui, -apple-system, sans-serif',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: accent, flexShrink: 0 }} />
                <div style={{ fontWeight: 600, fontSize: 13, lineHeight: '19px', wordBreak: 'break-word' }}>{t.message}</div>
              </div>
              {t.description && (
                <div
                  style={{
                    marginTop: 4,
                    marginLeft: 16,
                    fontSize: 12,
                    color: theme.colors.text.secondary,
                    lineHeight: '17px',
                    wordBreak: 'break-word',
                  }}
                >
                  {t.description}
                </div>
              )}
              {t.action && (
                <button
                  type="button"
                  onClick={() => {
                    onDismiss(t.id);
                    t.action?.onClick();
                  }}
                  style={{
                    marginTop: 8,
                    marginLeft: 16,
                    background: 'transparent',
                    border: `1px solid ${theme.colors.border.light}`,
                    borderRadius: 6,
                    color: theme.colors.interactive.primary,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '4px 10px',
                  }}
                >
                  {t.action.label}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(t.id)}
              aria-label="Dismiss notification"
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.colors.text.tertiary,
                cursor: 'pointer',
                fontSize: 15,
                padding: '0 4px',
                minWidth: 32,
                minHeight: 32,
              }}
            >
              ✕
            </button>
          </div>
        );
      })}
      <style>{`
        @keyframes tc-toast-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
